import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Search, ArrowLeft, Crown, RotateCcw, Ban, CheckCircle2, Users, Zap, MessageSquare, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function AdminPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [stats, setStats] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const r = await api.get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setUsers(r.data.users);
    const s = await api.get("/admin/stats"); setStats(s.data);
    const a = await api.get("/admin/audit"); setAudit(a.data.logs);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const grant = async (user_id, days) => {
    setBusy(user_id);
    try {
      await api.post("/admin/grant-premium", { user_id, days });
      toast.success(days ? `Premium granted for ${days} days` : "Lifetime granted");
      load();
    } catch (e) { toast.error("Failed"); } finally { setBusy(null); }
  };
  const revoke = async (user_id) => { setBusy(user_id); try { await api.post("/admin/revoke-premium", { user_id }); toast.success("Revoked"); load(); } finally { setBusy(null); } };
  const reset = async (user_id) => { setBusy(user_id); try { await api.post("/admin/reset-usage", { user_id }); toast.success("Usage reset"); load(); } finally { setBusy(null); } };
  const suspend = async (user_id) => { setBusy(user_id); try { await api.post("/admin/suspend", { user_id }); toast.success("Toggled suspension"); load(); } finally { setBusy(null); } };

  return (
    <div className="min-h-screen p-4 lg:p-8" data-testid="admin-page">
      <div className="aura-bloom" />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex items-center gap-3 mb-6">
          <button data-testid="admin-back-btn" onClick={() => nav("/app")} className="aura-btn-ghost w-10 h-10 rounded-full flex items-center justify-center"><ArrowLeft className="w-5 h-5" /></button>
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-violet-300 font-semibold">AfterMind · Admin Console</div>
            <h1 className="text-3xl font-bold aura-gradient-text">Dashboard</h1>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
          {[
            { label: "Total users", val: stats?.total_users, icon: Users, color: "text-cyan-300" },
            { label: "Premium", val: stats?.premium_users, icon: Crown, color: "text-amber-300" },
            { label: "Conversations", val: stats?.conversations, icon: MessageSquare, color: "text-violet-300" },
            { label: "Messages", val: stats?.messages, icon: Zap, color: "text-pink-300" },
            { label: "Generations", val: stats?.generations, icon: Sparkles, color: "text-emerald-300" },
          ].map((s, i) => (
            <div key={i} className="glass rounded-2xl p-4">
              <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
              <div className="text-2xl font-bold text-slate-100">{s.val ?? "—"}</div>
              <div className="text-xs text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 glass-strong rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-100">Users</h2>
              <div className="relative w-64">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search email or name" className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-100 outline-none focus:border-violet-400" />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.15em] text-slate-500 border-b border-white/5">
                    <th className="py-2">User</th><th>Plan</th><th>Images</th><th>State</th><th className="text-right pr-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-white/5" data-testid={`user-row-${u.id}`}>
                      <td className="py-3">
                        <div className="text-slate-100 font-medium">{u.name}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </td>
                      <td>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${
                          u.plan === "lifetime" ? "bg-amber-500/20 text-amber-300" :
                          u.plan === "premium" ? "bg-violet-500/25 text-violet-200" : "bg-slate-700 text-slate-300"
                        }`}>{u.plan}</span>
                      </td>
                      <td className="text-slate-300 text-xs">{u.image_used_today}/{5} today</td>
                      <td>{u.suspended ? <span className="text-xs text-red-300">Suspended</span> : <span className="text-xs text-emerald-300">Active</span>}</td>
                      <td className="text-right pr-2">
                        <div className="inline-flex items-center gap-1">
                          <button data-testid={`grant-30-${u.id}`} disabled={busy === u.id} onClick={() => grant(u.id, 30)} className="px-2 py-1 text-xs rounded-md bg-violet-500/20 text-violet-200 hover:bg-violet-500/30">+30d</button>
                          <button data-testid={`grant-lifetime-${u.id}`} disabled={busy === u.id} onClick={() => grant(u.id, null)} className="px-2 py-1 text-xs rounded-md bg-amber-500/20 text-amber-200 hover:bg-amber-500/30">Lifetime</button>
                          <button data-testid={`revoke-${u.id}`} disabled={busy === u.id} onClick={() => revoke(u.id)} className="px-2 py-1 text-xs rounded-md bg-slate-700 text-slate-200 hover:bg-slate-600">Revoke</button>
                          <button data-testid={`reset-${u.id}`} disabled={busy === u.id} onClick={() => reset(u.id)} className="p-1 rounded-md text-slate-400 hover:text-white" title="Reset usage"><RotateCcw className="w-3.5 h-3.5" /></button>
                          <button data-testid={`suspend-${u.id}`} disabled={busy === u.id} onClick={() => suspend(u.id)} className="p-1 rounded-md text-slate-400 hover:text-red-400" title="Suspend/Reactivate">{u.suspended ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && <div className="text-center text-slate-500 text-sm py-8">No users found</div>}
            </div>
          </div>

          <div className="glass-strong rounded-3xl p-5">
            <h2 className="text-lg font-bold text-slate-100 mb-4">Audit log</h2>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {audit.map((a) => (
                <div key={a.id} className="glass rounded-lg px-3 py-2 text-xs">
                  <div className="text-slate-100 font-semibold">{a.action}</div>
                  <div className="text-slate-400">{a.target}</div>
                  <div className="text-slate-500 text-[10px] mt-0.5">{a.created_at?.slice(0, 19).replace("T", " ")}</div>
                </div>
              ))}
              {audit.length === 0 && <div className="text-slate-500 text-sm text-center py-4">No actions logged</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
